import { useMemo } from "react";
import styled, { useTheme } from "styled-components";
import { Text } from "@chakra-ui/react";
import { T } from "src/apps/components/i18n/Translation";
import { Select } from "src/apps/components/select/Select";
import { useTranslations } from "src/apps/hooks/useTranslations";
import { useIdentityVerification } from "../../../hooks/identityVerification/useIdentityVerification";
import { useDocumentationArea } from "src/apps/hooks/useDocumentationArea";
import { useIsMedia } from "src/apps/hooks/useIsMedia";
import { TextBold } from "src/apps/components/typography/Texts";
import { TEST_IDS } from "src/apps/config/testIds";

interface IdentityVerificationCountrySelectorProps {
  onSelectScrollBehaviour: () => void;
}

export const IdentityVerificationCountrySelector = ({
  onSelectScrollBehaviour,
}: IdentityVerificationCountrySelectorProps) => {
  const { blackColor } = useTheme();
  const { allowedPairs, selectedDocumentType, selectCountry, selectedCountry } =
    useIdentityVerification();
  const { t } = useTranslations();
  const { isMobile } = useIsMedia();
  const { identityVerifications } = useDocumentationArea();

  const documentTypeIndex = allowedPairs.findIndex(
    (pair) => pair.documentType === selectedDocumentType,
  );

  const availableCountries = allowedPairs[documentTypeIndex]?.countries ?? [];

  const onCountrySelected = (countryValue: string) => {
    if (!countryValue || countryValue === selectedCountry) return;

    const match = availableCountries.find(({ value }) => value === countryValue);

    if (!match) {
      throw new Error("Country value not found");
    }

    selectCountry(match.value);
    onSelectScrollBehaviour();
  };

  const selectedCountryValue = useMemo(
    () => availableCountries?.find((country) => country.value === selectedCountry)?.value ?? null,
    [availableCountries, selectedCountry],
  );

  const isCompleted = identityVerifications[0].hasValidResponses();

  if (isCompleted) return null;

  return (
    <Spacer>
      <CountrySelectorContainer>
        {isMobile ? (
          <TextBold>
            <T k="documentation_area.identity_verification.select_country.title" />
          </TextBold>
        ) : (
          <Text>
            <T k="documentation_area.identity_verification.select_country.title" />
          </Text>
        )}
        <Select
          dataTestId={TEST_IDS.identityVerification.countrySelect}
          placeholder={t("common.dropdown.placeholder")}
          options={
            availableCountries.map((country) => {
              return {
                key: country.label,
                value: country.value,
              };
            }) ?? []
          }
          setSelectedValue={(countryValue) => onCountrySelected(countryValue)}
          color={blackColor}
          value={selectedCountryValue ?? ""}
        />
      </CountrySelectorContainer>
    </Spacer>
  );
};

const Spacer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const CountrySelectorContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.5rem;
`;
